import React, { useCallback, useEffect, useState } from 'react';
import { AuthUser, Role, listUsers, createUser, deleteUser, getStoredUser } from '../api/auth';
import './Users.css';

const Users: React.FC = () => {
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('user');
  const [creating, setCreating] = useState(false);

  const me = getStoredUser();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await listUsers());
    } catch (err: any) {
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      await createUser(username.trim(), password, role);
      setUsername('');
      setPassword('');
      setRole('user');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete user ${name}? Their voice notes will remain but be inaccessible.`)) {
      return;
    }
    setError(null);
    try {
      await deleteUser(id);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete user');
    }
  };

  return (
    <div className="users-page">
      <h1>User accounts</h1>
      <p className="users-intro">
        Each user signs in with their own account and sees only their own voice notes.
      </p>

      {error && <div className="users-error">{error}</div>}

      <form className="users-form" onSubmit={handleCreate}>
        <h2>Add a user</h2>
        <div className="users-form-row">
          <label className="users-field">
            <span>Email / username</span>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="name@example.com"
              autoComplete="off"
              required
            />
          </label>
          <label className="users-field">
            <span>Password</span>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="min 6 characters"
              autoComplete="new-password"
              required
            />
          </label>
          <label className="users-field users-field--role">
            <span>Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <button className="users-add" type="submit" disabled={creating}>
            {creating ? 'Adding…' : 'Add user'}
          </button>
        </div>
      </form>

      <div className="users-list">
        <h2>Accounts {users.length > 0 && <span className="users-count">({users.length})</span>}</h2>
        {loading ? (
          <div className="users-loading">Loading…</div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.username}</td>
                  <td>
                    <span className={`users-badge users-badge--${u.role}`}>{u.role}</span>
                  </td>
                  <td>{u.created_at ? new Date(u.created_at).toLocaleDateString('en-AU') : '—'}</td>
                  <td className="users-actions">
                    {me?.id === u.id ? (
                      <span className="users-self">you</span>
                    ) : (
                      <button className="users-del" onClick={() => handleDelete(u.id, u.username)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Users;
